/* nx-cart.js — the two things the cart drawer needs that its own script cannot give it.
 *
 * ⚠ The drawer's behaviour lives in `assets/main.js`, which came with the purchased base and is
 * OBFUSCATED — string-array indirection, no readable identifiers. It is not a file to patch. What
 * it does expose is a public `open()` / `close()` on the `<cart-drawer>` element, and that is
 * enough to add behaviour from outside it.
 */
(function () {
  if (window.__nxCartJs) return;
  window.__nxCartJs = true;

  function drawer() { return document.querySelector('cart-drawer'); }
  function isOpen(d) { return !!(d && d.classList.contains('active')); }

  /* ── close on a click outside the panel ───────────────────────────────────────────────────────
     The theme already binds this — in the CartDrawer constructor, on `#CartDrawer-Overlay`. The
     trouble is WHERE that element lives: `<div id="CartDrawer">` wraps it, and every repaint of
     the cart replaces that div's innerHTML —

         nx-quick-add.js  paintCart()   liveInner.innerHTML = freshInner.innerHTML
         nx-set.js        the set add, which repaints the drawer rather than just opening it
         the theme editor  a section re-render

     — so the overlay a shopper clicks on after adding anything is a NEW node, and the listener is
     still bound to the one that was thrown away. The drawer then ignores every click outside it
     while Escape and the × keep working, because those are bound to the element itself. That
     asymmetry is the signature of this fault.

     Binding to `document` instead makes the fix independent of node identity: it cannot be undone
     by a repaint from any source, present or future. Closing twice is harmless — `close()` only
     removes a class — so this coexists with the theme's own handler rather than replacing it. */
  document.addEventListener('click', function (e) {
    var d = drawer();
    if (!isOpen(d)) return;
    var t = e.target;
    if (!t || !t.closest) return;
    /* a control the drawer's own repaint replaced mid-tap is no longer in the page: not a click outside */
    if (!t.isConnected) return;
    if (t.closest('.drawer__inner')) return;
    /* The control that opens it must not also close it. `open()` sets `active` in a setTimeout, so
       the opening click bubbles to here BEFORE the class lands and would be ignored anyway — but
       relying on that ordering is relying on someone else's implementation detail. */
    if (t.closest('#cart-icon-bubble, .nxs-qa, cart-notification')) return;
    if (typeof d.close === 'function') d.close();
  });

  /* ── an object in the cart is a door to its page (owner, 2026-09-29) ─────────────────────────
     The picture and the name are links now (snippets/cart-drawer.liquid). The drawer closes on the
     way, so Back does not restore the page with it still open; and when the link is the page the
     reader is already on, closing IS the whole answer — reloading it would only lose their place. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('.cart-drawer a.cart-item__name, .cart-drawer a.cart-item__link');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    var d = drawer();
    if (d && typeof d.close === 'function') d.close();
    var u = new URL(a.href, location.href);
    if (u.pathname === location.pathname && u.search === location.search) e.preventDefault();
  });

  /* ── say that the list goes on ────────────────────────────────────────────────────────────────
     A cart of four objects hides ~260px of itself at 1440x780 and says nothing about it: the list
     simply ends at the edge of its box, which reads as the whole cart. Worst on a phone, where
     there is no scrollbar to notice at all.

     A veil over the last 56px, shown only while there is something below it and fading out at the
     end of the travel. ⚠ It is drawn on `.drawer__contents`, the wrapper's PARENT, because the
     wrapper is the thing that scrolls — a pseudo-element on it would scroll away with the list. */
  function syncOverflow() {
    var box = document.querySelector('.cart-drawer .drawer__contents');
    var wrap = document.querySelector('.cart-drawer .drawer__cart-items-wrapper');
    if (!box || !wrap) return;
    var more = wrap.scrollHeight - wrap.clientHeight - wrap.scrollTop > 4;
    box.classList.toggle('nx-has-more', more);
  }

  /* `scroll` does not bubble, but it does capture. One listener on the document therefore keeps
     working when the scrolling element itself is replaced. */
  document.addEventListener('scroll', syncOverflow, true);
  window.addEventListener('resize', syncOverflow, { passive: true });
  document.addEventListener('click', function () { setTimeout(syncOverflow, 60); });

  /* Every repaint changes how much there is to scroll, and a repaint is the one moment nothing
     else fires. Observing childList only — the class this writes is an attribute, so it cannot
     retrigger its own observer. */
  function watch() {
    var d = drawer();
    if (!d || !window.MutationObserver) return;
    new MutationObserver(function () { chooseSuggestion(); syncOverflow(); reportView(); }).observe(d, { childList: true, subtree: true });
  }

  /* ── the pair the cart suggests ───────────────────────────────────────────────────────────────
     snippets/nx-cart-pair.liquid draws the object that goes with what is already in the cart and
     states what it adds to the total. Two things have to happen from here rather than from there:

     ⚠ The tap is bound by DELEGATION on the document. The drawer repaints by innerHTML on every
     cart change, so a listener bound to the button — or a script written inside the snippet —
     is thrown away with the node, and a script inserted through innerHTML never runs at all.

     ⚠ The view is reported on each PAINT, not on page load, because the block appears and
     changes as the cart does. `promotion_id` carries the pair's partner and `creative_slot` says
     where, so the cart's suggestions can be read against the product page's in one report. */
  function send(name, params) {
    try {
      if (typeof window.gtag === 'function') { window.gtag('event', name, params); return; }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) { /* analytics never breaks the cart */ }
  }

  function promo(el, name) {
    var handle = el.getAttribute('data-nx-pair-handle') ||
      (el.closest('[data-nx-cart-pair]') || {}).getAttribute?.('data-nx-pair-handle') || '';
    send(name, {
      promotion_id: 'cartpair:' + handle,
      promotion_name: 'cart_pair',
      creative_slot: 'cart_drawer',
      items: [{ item_id: handle }],
    });
  }

  /* ── the cart's suggestions ────────────────────────────────────────────────────────────────
     From 2026-09-23 to 2026-10-02 only one of the pair and "give one too" survived, by a toss
     remembered for the session (owner: two offers on one object read as a fair). The owner reversed
     it from a frame on 2026-10-02: with one object in the cart, "One more as a gift" and the pair
     card stand together, and the drawer's own row and both cards still fit 390x664 unscrolled. */
  /* ⚠ NO SESSION FLAG ANY MORE (owner, 2026-10-06). Since 2026-09-23 an offer just taken stayed closed while the cart
     was bigger than at the tap. With "One more as a gift" offered at one object and the third at two of the same
     (snippets/nx-cart-give.liquid), that flag shut the third the moment the gift was taken — the owner saw two in the
     cart and no offer. Liquid decides every offer from the cart itself, and three is the top step, so nothing here
     needs to remember a tap. Only the old "second of the same" block (/cart) still outranks the gift card. */
  function chooseSuggestion() {
    var give = document.querySelector('[data-nx-cart-give]');
    if (give && document.querySelector('[data-nx-cart-bundle]')) give.remove();
  }

  var lastSeen = '';
  var lastGift = '';
  function reportView() {
    var bundle = document.querySelector('[data-nx-cart-bundle]');
    if (bundle) {
      var bh = 'bundle:' + (bundle.getAttribute('data-nx-bundle-handle') || '');
      if (bh === lastSeen) return;
      lastSeen = bh;
      send('view_promotion', {
        promotion_id: bh, promotion_name: 'second_of_the_same', creative_slot: 'cart_drawer',
        items: [{ item_id: bundle.getAttribute('data-nx-bundle-handle') || '' }],
      });
      return;
    }
    var pair = document.querySelector('[data-nx-cart-pair]');
    /* the pair and the gift stand together since 2026-10-02: each is reported once per showing, not per repaint */
    var handle = pair ? pair.getAttribute('data-nx-pair-handle') || '' : '';
    if (handle !== lastSeen) {
      lastSeen = handle;
      if (pair) promo(pair, 'view_promotion');
    }
    var give = document.querySelector('[data-nx-cart-give]');
    var gh = give ? 'give:' + (give.getAttribute('data-nx-give-handle') || '') + ':' + (give.getAttribute('data-nx-give-step') || '') : '';
    if (gh === lastGift) return;
    lastGift = gh;
    if (!give) return;
    send('view_promotion', {
      promotion_id: gh, promotion_name: 'one_more_as_a_gift', creative_slot: 'cart_drawer',
      items: [{ item_id: give.getAttribute('data-nx-give-handle') || '' }],
    });
  }

  /* "One more as a gift": the object again, in the colour ringed on the card */
  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-nx-give-add]');
    if (!chip || chip.disabled) return;
    var id = chip.getAttribute('data-nx-give-add');
    var qty = parseInt(chip.getAttribute('data-nx-give-qty'), 10) || 1;
    if (!id || !window.nxCart) return;
    chip.disabled = true;
    var block = chip.closest('[data-nx-cart-give]');
    if (block) {
      send('select_promotion', {
        promotion_id: 'give:' + (block.getAttribute('data-nx-give-handle') || ''),
        promotion_name: 'one_more_as_a_gift', creative_slot: 'cart_drawer',
        items: [{ item_id: block.getAttribute('data-nx-give-handle') || '', quantity: qty }],
      });
    }
    window.nxCart.add([{ id: Number(id), quantity: qty }], 'Added')
      .catch(function () { chip.disabled = false; });
  });

  /* one more of the same object, at the rate the ladder gives the basket it would make */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-nx-bundle-add]');
    if (!btn || btn.disabled) return;
    var id = btn.getAttribute('data-nx-bundle-add');
    if (!id || !window.nxCart) return;
    btn.disabled = true;
    var block = btn.closest('[data-nx-cart-bundle]');
    if (block) {
      send('select_promotion', {
        promotion_id: 'bundle:' + (block.getAttribute('data-nx-bundle-handle') || ''),
        promotion_name: 'second_of_the_same', creative_slot: 'cart_drawer',
        items: [{ item_id: block.getAttribute('data-nx-bundle-handle') || '', quantity: 1 }],
      });
    }
    window.nxCart.add([{ id: Number(id), quantity: 1 }], btn.getAttribute('data-nx-bundle-name') + ' · added')
      .catch(function () { btn.disabled = false; });
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-nx-pair-add]');
    if (!btn || btn.disabled) return;
    var id = btn.getAttribute('data-nx-pair-add');
    if (!id || !window.nxCart) return;
    btn.disabled = true;
    var block = btn.closest('[data-nx-cart-pair]');
    if (block) promo(block, 'select_promotion');
    window.nxCart.add([{ id: Number(id), quantity: 1 }], btn.getAttribute('data-nx-pair-name') + ' · added')
      .catch(function () { btn.disabled = false; });
  });

  /* ── a line of the cart: its counter and Remove (owner, 2026-10-01, after Native Union's drawer) ───────────
     snippets/nx-cart-row.liquid prints a row per line with "− n +" and Remove; nothing in it changes the variant.
     The number answers the tap at once and the request waits for the thumb to stop (a quick "+ + +" is one call
     for three, not three calls racing); Remove fades the row and goes at once. Shopify puts a changed line first and
     a repainted drawer loses its scroll, so the drawer is PATCHED: the rows the answer brings are written into the
     same nodes, position by position, in the order the reader was looking at — no row is rebuilt, no picture
     reloads, the list keeps its scroll.
     ⚠ EVERY CHANGE WAITS ITS TURN. Two requests in flight read the same cart and the second wrote over the first
     (01.10, the dots): one queue, and a job reads the drawer when its turn comes, not when it was tapped. */
  var nxQueue = Promise.resolve();
  function nxLater(job) { nxQueue = nxQueue.then(job, job); return nxQueue; }
  function nxRows(root) { return Array.prototype.slice.call(root.querySelectorAll('.cart-items > .nx-row')); }
  function nxMorph(a, b) {
    if (a.nodeType !== b.nodeType || a.nodeName !== b.nodeName) { a.parentNode.replaceChild(b.cloneNode(true), a); return; }
    if (a.nodeType === 3 || a.nodeType === 8) { if (a.nodeValue !== b.nodeValue) a.nodeValue = b.nodeValue; return; }
    if (a.nodeType !== 1) return;
    /* ⚠ The wallet row (Meta entrance, 2026-10-21) draws its own buttons over the skeleton Shopify prints: morphed back
       to the skeleton after "+" or Remove it stood empty. Left alone while Shopify prints it the same (same cart-id),
       replaced whole, to draw itself again, when not. */
    if (a.nodeName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') {
      var same = a.attributes.length === b.attributes.length && Array.prototype.every.call(b.attributes, function (x) { return a.getAttribute(x.name) === x.value; });
      if (!same) a.parentNode.replaceChild(b.cloneNode(true), a);
      return;
    }
    Array.prototype.slice.call(a.attributes).forEach(function (x) { if (!b.hasAttribute(x.name)) a.removeAttribute(x.name); });
    Array.prototype.slice.call(b.attributes).forEach(function (x) { if (a.getAttribute(x.name) !== x.value) a.setAttribute(x.name, x.value); });
    /* whitespace between tags is not content: skipping it keeps row matched to row */
    var real = function (n) { return !(n.nodeType === 3 && !/\S/.test(n.nodeValue)) && n.nodeType !== 8; };
    var ac = Array.prototype.slice.call(a.childNodes).filter(real), bc = Array.prototype.slice.call(b.childNodes).filter(real);
    for (var i = 0; i < bc.length; i++) { if (i < ac.length) nxMorph(ac[i], bc[i]); else a.appendChild(bc[i].cloneNode(true)); }
    for (var j = bc.length; j < ac.length; j++) a.removeChild(ac[j]);
  }
  /* want: the variant of each row, top to bottom, as it should be after the change;
     keep: the live row nodes that stay, in the same order — row i of the answer is written into keep[i] */
  function nxPatch(sections, want, keep) {
    var html = sections && sections['cart-drawer'];
    var live = document.querySelector('#CartDrawer');
    if (!html || !live) { if (window.nxCart) window.nxCart.paint(sections); return; }
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('#CartDrawer');
    /* cut to the same set as the live row, so nxMorph sees the same wallet element and leaves it drawn */
    if (fresh && window.nxWallets) window.nxWallets(fresh);
    var liveUl = live.querySelector('.cart-items'), freshUl = fresh && fresh.querySelector('.cart-items');
    /* the cart emptied, or the drawer changed its shape: the theme's own repaint knows the empty state */
    if (!fresh || !liveUl || !freshUl) { if (window.nxCart) window.nxCart.paint(sections); return; }
    var pool = nxRows(fresh), ordered = [];
    want.forEach(function (id) {
      for (var k = 0; k < pool.length; k++) if (pool[k].getAttribute('data-nx-variant') === id) { ordered.push(pool.splice(k, 1)[0]); return; }
    });
    ordered = ordered.concat(pool);
    if (!ordered.length) { if (window.nxCart) window.nxCart.paint(sections); return; }
    var list = document.querySelector('.cart-drawer .drawer__cart-items-wrapper');
    var top = list ? list.scrollTop : 0;
    nxRows(live).forEach(function (r) { if (keep.indexOf(r) < 0) r.parentNode.removeChild(r); });
    ordered.forEach(function (f, i) {
      var node = keep[i];
      /* a row whose own change is still waiting keeps the number the reader gave it; its own answer writes it */
      if (node && node.classList.contains('is-wait')) { node.setAttribute('data-nx-line', f.getAttribute('data-nx-line')); }
      else if (node) nxMorph(node, f);
      else { node = f.cloneNode(true); }
      var at = nxRows(liveUl)[i];
      if (at !== node) liveUl.insertBefore(node, at || null);
    });
    nxRows(liveUl).slice(ordered.length).forEach(function (r) { r.parentNode.removeChild(r); });
    freshUl.parentNode.replaceChild(liveUl.cloneNode(true), freshUl);
    nxMorph(live, fresh);
    var after = document.querySelector('.cart-drawer .drawer__cart-items-wrapper');
    if (after) after.scrollTop = top;
    var bubble = sections['cart-icon-bubble'] && new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
    var fb = bubble && (bubble.querySelector('#shopify-section-cart-icon-bubble') || bubble.querySelector('#cart-icon-bubble'));
    var lb = document.querySelector('#cart-icon-bubble');
    if (fb && lb) lb.innerHTML = fb.innerHTML;
  }
  /* ⚠ THE RECOUNT SHOWS AT ONCE (owner, 2026-10-06: "почему не пересчитывается количество товара в корзине, когда я
     убираю какое-то количество"). The counter's number moved on the tap, but the heading, the button's total and the
     offer cards waited for Shopify's answer — 350 ms for the thumb to stop and then the network, a second or two on a
     phone — and Checkout could be pressed on the old sum. Now, while any row waits, the heading counts the rows' own
     numbers and the footer (offers, total, Checkout) dims and takes no tap; Shopify's answer (nxPatch) or its refusal
     (nxFail) writes the real numbers and lifts it. */
  function nxPending() {
    var d = document.querySelector('#CartDrawer');
    if (!d) return;
    var n = 0, busy = false;
    nxRows(d).forEach(function (r) {
      var gone = r.classList.contains('is-busy'), wait = r.classList.contains('is-wait');
      if (gone || wait) busy = true;
      if (!gone) n += wait && typeof r.__nxWant === 'number' ? r.__nxWant : (parseInt(r.getAttribute('data-nx-qty'), 10) || 0);
    });
    var h = d.querySelector('.drawer__heading');
    if (h && busy) h.textContent = h.textContent.replace(/\d+\s+items?/, n + (n === 1 ? ' item' : ' items'));
    d.classList.toggle('is-recount', busy);
  }
  function nxCartPost(url, body) {
    body.sections = 'cart-drawer,cart-icon-bubble';
    body.sections_url = window.location.pathname;
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { if (!r.ok) throw new Error(url + ' ' + r.status); return r.json(); });
  }
  function nxFail() {
    /* the drawer is put back to what Shopify holds, so nothing on screen claims a change that did not happen */
    var lift = function () { var d = document.querySelector('#CartDrawer'); if (d) d.classList.remove('is-recount'); };
    fetch('/?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (s) { if (s && window.nxCart) window.nxCart.paint(s); lift(); })
      .catch(lift);
    if (window.nxCart && window.nxCart.toast) window.nxCart.toast('That did not go through. Try once more.');
  }
  /* send the row's number: its line to that quantity, 0 takes it away */
  function nxSend(row) {
    nxLater(function () {
      if (!row.isConnected) { nxPending(); return; }
      var q = row.__nxWant;
      row.classList.remove('is-wait');
      var rows = nxRows(document.querySelector('#CartDrawer') || document);
      var keep = q > 0 ? rows : rows.filter(function (r) { return r !== row; });
      var want = keep.map(function (r) { return r.getAttribute('data-nx-variant'); });
      if (q > 0 && q === (parseInt(row.getAttribute('data-nx-qty'), 10) || 0)) { nxPending(); return; }
      return nxCartPost('/cart/change.js', { id: row.getAttribute('data-nx-line'), quantity: Math.max(0, q) })
        .then(function (res) { nxPatch(res.sections, want, keep); nxPending(); })
        .catch(nxFail);
    });
  }

  /* the counter: the number moves at once, the request goes when the thumb has stopped for a moment */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-nx-step]');
    if (!btn || btn.disabled) return;
    var row = btn.closest('.nx-row');
    if (!row || row.classList.contains('is-busy')) return;
    var shown = row.querySelector('.nx-step__n');
    var now = typeof row.__nxWant === 'number' && row.classList.contains('is-wait') ? row.__nxWant : (parseInt(row.getAttribute('data-nx-qty'), 10) || 1);
    var q = Math.max(0, now + (parseInt(btn.getAttribute('data-nx-step'), 10) || 0));
    row.__nxWant = q;
    if (shown) shown.textContent = String(q);
    if (q === 0) { row.classList.remove('is-wait'); row.classList.add('is-busy'); clearTimeout(row.__nxTimer); nxPending(); nxSend(row); return; }
    row.classList.add('is-wait');
    nxPending();
    clearTimeout(row.__nxTimer);
    row.__nxTimer = setTimeout(function () { nxSend(row); }, 350);
  });

  /* Remove: the line goes at once */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-nx-remove]');
    if (!btn) return;
    var row = btn.closest('.nx-row');
    if (!row || row.classList.contains('is-busy')) return;
    clearTimeout(row.__nxTimer);
    row.__nxWant = 0;
    row.classList.remove('is-wait');
    row.classList.add('is-busy');
    nxPending();
    nxSend(row);
  });

  /* a card's choice before its ADD — the pair's sets, the gift's colour: the button takes the variant ringed */
  document.addEventListener('click', function (e) {
    var kinds = [['data-nx-pair-pick', '[data-nx-cart-pair]', 'data-nx-pair-add'], ['data-nx-give-pick', '[data-nx-cart-give]', 'data-nx-give-add']];
    for (var k = 0; k < kinds.length; k++) {
      var pick = e.target.closest && e.target.closest('[' + kinds[k][0] + ']');
      if (!pick) continue;
      var card = pick.closest(kinds[k][1]);
      var add = card && card.querySelector('[' + kinds[k][2] + ']');
      if (!add) return;
      add.setAttribute(kinds[k][2], pick.getAttribute(kinds[k][0]));
      card.querySelectorAll('[' + kinds[k][0] + ']').forEach(function (p) { var on = p === pick; p.classList.toggle('is-on', on); p.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      /* the gift card's photograph follows the colour ringed (snippets/nx-cart-give.liquid, data-nx-give-img) */
      var photo = card.querySelector('.nxgift__photo'), src = pick.getAttribute('data-nx-give-img');
      if (photo && src) photo.src = src;
      return;
    }
  });

  function boot() { watch(); chooseSuggestion(); syncOverflow(); reportView(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
